const TAB_LINK_SELECTORS = [
  '.nav-tabs .nav-link',
  '.nav-pills .nav-link',
  '.bh-main-tabs__link'
];

const normalizeLabel = (text) =>
  (text || '').replace(/\s+/g, ' ').trim();

/** Find a visible tab control whose label includes the given text. */
export const findTabElementByLabel = (tabLabel) => {
  if (!tabLabel) {
    return null;
  }
  const needle = normalizeLabel(tabLabel).toLowerCase();
  for (const selector of TAB_LINK_SELECTORS) {
    const links = document.querySelectorAll(selector);
    const match = Array.from(links).find((el) => {
      if (el.offsetParent === null && !el.getClientRects().length) {
        return false;
      }
      return normalizeLabel(el.textContent).toLowerCase().includes(needle);
    });
    if (match) {
      return match;
    }
  }
  return null;
};

export const findElementBySelector = (selector) => {
  if (!selector) {
    return null;
  }
  const el = document.querySelector(selector);
  if (!el || (el.offsetParent === null && !el.getClientRects().length)) {
    return null;
  }
  return el;
};

/** Find a visible button/link whose label includes the given text. */
export const findButtonByLabel = (buttonLabel, scopeSelector = null) => {
  if (!buttonLabel) {
    return null;
  }
  const root = scopeSelector ? document.querySelector(scopeSelector) : document;
  if (!root) {
    return null;
  }
  const needle = normalizeLabel(buttonLabel).toLowerCase();
  const buttons = root.querySelectorAll('button, a.btn, .btn');
  return Array.from(buttons).find((el) => {
    if (el.offsetParent === null && !el.getClientRects().length) {
      return false;
    }
    return normalizeLabel(el.textContent).toLowerCase().includes(needle);
  }) || null;
};

/** Resolve the DOM node to highlight for a tour step. */
export const findTourStepTarget = (step) => {
  if (!step) {
    return null;
  }
  const scope = step.scopeSelector || null;
  if (step.buttonLabel) {
    return findButtonByLabel(step.buttonLabel, scope);
  }
  if (step.selector) {
    const scopedRoot = scope ? document.querySelector(scope) : document;
    if (!scopedRoot) {
      return null;
    }
    const el = scopedRoot.querySelector(step.selector) || (scope ? null : findElementBySelector(step.selector));
    if (!el || (el.offsetParent === null && !el.getClientRects().length)) {
      return null;
    }
    return el;
  }
  if (step.tabLabel) {
    return findTabElementByLabel(step.tabLabel);
  }
  return null;
};

export const activateTabForStep = (step) => {
  if (!step?.tabLabel) {
    return false;
  }
  const tabEl = findTabElementByLabel(step.tabLabel);
  if (!tabEl) {
    return false;
  }
  activateTabElement(tabEl);
  return true;
};

export const activateTabElement = (el) => {
  if (!el || el.classList.contains('active')) {
    return;
  }
  el.click();
};

export const getHighlightRect = (el, padding = 8) => {
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2
  };
};

export const getTooltipPosition = (rect, tooltipWidth = 360, tooltipHeight = 200) => {
  if (!rect) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const margin = 16;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let top = rect.top + rect.height + margin;
  let left = rect.left + rect.width / 2 - tooltipWidth / 2;

  if (top + tooltipHeight > viewportH - margin) {
    top = rect.top - tooltipHeight - margin;
  }
  if (left < margin) {
    left = margin;
  }
  if (left + tooltipWidth > viewportW - margin) {
    left = viewportW - tooltipWidth - margin;
  }
  if (top < margin) {
    top = margin;
  }

  return { top: `${top}px`, left: `${left}px`, transform: 'none' };
};
